import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { StrategyChart, DatasetConfig } from './StrategyChart';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { TimeGranularity } from './StrategiesUtils';
import { useBaseHedgeMaterials, useDerivedMaterials } from '../../../hooks/useBaseHedgeMaterials';
import { useAccountStore } from '@components/Stores/accountStore';
import { getTerminalAllocations, TerminalAllocation } from '../../../services/hedgesService';
import { Code } from '@mui/icons-material';
import { ChartControls } from './ChartControls';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip as MuiTooltip } from '@mui/material';

export interface HedgesDerivedData {
  terminal: string;
  derivedExposures: Record<string, number>;
  calculationDetails?: Record<string, {
    demand: number;
    buffer: number;
    supply: number;
    startStorage: number;
    endStorage: number;
    finalExposure: number;
    spotPrice: number;
    ratio: number;
    monetaryScore: number;
  }>;
  percentages?: Record<string, number>;
  absoluteMonth?: number; // For terminal filtering by year toggle
}

interface Props {
  accountId: number;
  baseMaterialId: string;
  baseMaterialPrice: number;
  unit?: IMassUnitObject;
  terminals?: number[] | null;
  onTimeChange?: (_time: TimeGranularity) => void;
  timeGranularity?: TimeGranularity;
  onMonthSelect?: (_index: number) => void;
  selectedIndex?: number;
  onDataCalculated?: (_data: {
    chartData: any[];
    derivedMaterials: Array<{ formula_code: string; formula_name: string; price_ratio: number }>;
    formulaColorDictionary: Record<string, string>;
  }) => void;
  onSaveAction?: (_action: { save: () => Promise<void>; isSaving: boolean; canSave: boolean }) => void; // kept for API compat, no longer functional
}

export const HedgesDeriving: React.FC<Props> = ({
  accountId,
  baseMaterialId,
  baseMaterialPrice,
  unit,
  terminals,
  onMonthSelect,
  selectedIndex,
  onTimeChange,
  timeGranularity = 'Month',
  onDataCalculated,
  onSaveAction,
}) => {
  const baseCurrency = useBaseCurrency();
  const { materialsMap } = useMaterialsStore();
  const { formulaColorDictionary, account } = useAccountStore();
  const { data: formulasPricesData } = useFormulasPrices();

  // Fetch base material template and derived materials
  const { data: baseHedgeMaterials } = useBaseHedgeMaterials(accountId);
  const baseTemplate = baseHedgeMaterials?.[0];
  const templateId = baseTemplate?.id ? parseInt(baseTemplate.id, 10) : undefined;
  const { data: derivedMaterials } = useDerivedMaterials(templateId);

  const pricesMap = useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    const map: Record<string, number> = {};
    Object.entries(formulasPricesData.pricesMap).forEach(([code, info]) => {
      map[code] = info.price;
    });
    return map;
  }, [formulasPricesData]);

  const horizonMonths = account?.viewingHorizonMonths || 24;
  // Always fetch 24 months for the deriving chart so the 1/2-year toggle has data to filter
  const derivingHorizonMonths = Math.max(horizonMonths, 24);

  // Fetch terminal allocations from backend — single source of truth
  const { data: terminalAllocations = [] } = useQuery<TerminalAllocation[]>(
    ['hedge-derivation-allocations', accountId, Object.keys(pricesMap).length, derivingHorizonMonths],
    () => getTerminalAllocations(accountId, pricesMap, derivingHorizonMonths),
    {
      enabled: !!accountId && !!baseTemplate,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  );

  // Transform backend allocations into the HedgesDerivedData shape the chart expects
  const hedgesDerivedData = useMemo<HedgesDerivedData[]>(() => {
    if (!derivedMaterials || derivedMaterials.length === 0 || terminalAllocations.length === 0) {
      return [];
    }

    const result: HedgesDerivedData[] = [];
    const now = new Date();
    const nowMonths = now.getFullYear() * 12 + now.getMonth();

    // On-Hand: monetary value of starting inventory (startStorage × spotPrice × ratio)
    // Same units as terminal bars so it's directly comparable on the chart
    const firstAlloc = terminalAllocations[0];
    if (firstAlloc) {
      const onHandExposures: Record<string, number> = {};
      derivedMaterials.forEach(dm => {
        const mat = firstAlloc.materials[dm.formula_code];
        if (!mat) { onHandExposures[dm.formula_code] = 0; return; }
        onHandExposures[dm.formula_code] = mat.startStorage * mat.spotPrice * mat.ratio;
      });
      result.push({ terminal: 'On-Hand', derivedExposures: onHandExposures });
    }

    for (const alloc of terminalAllocations) {
      const derivedExposures: Record<string, number> = {};
      const calculationDetails: Record<string, any> = {};
      const percentages: Record<string, number> = {};

      // absoluteMonth = months from now to terminal end — used by StrategyChart year-toggle filter
      const endDate = new Date(alloc.endDate);
      const absoluteMonth = (endDate.getFullYear() * 12 + endDate.getMonth()) - nowMonths;

      for (const dm of derivedMaterials) {
        const fc = dm.formula_code;
        const mat = alloc.materials[fc];
        if (!mat) continue;
        derivedExposures[fc] = mat.monetaryScore;
        percentages[fc] = mat.allocationPct;
        calculationDetails[fc] = {
          demand: mat.demand,
          buffer: mat.buffer,
          supply: mat.supply,
          startStorage: mat.startStorage,
          endStorage: mat.endStorage,
          finalExposure: mat.finalExposure,
          spotPrice: mat.spotPrice,
          ratio: mat.ratio,
          monetaryScore: mat.monetaryScore,
        };
      }

      result.push({
        terminal: alloc.terminal,
        derivedExposures,
        calculationDetails,
        percentages,
        absoluteMonth,
      });
    }

    return result;
  }, [derivedMaterials, terminalAllocations]);

  const [showCalcModal, setShowCalcModal] = React.useState(false);

  // Signal parent that save is not applicable (derivation is always live)
  React.useEffect(() => {
    if (onSaveAction) {
      onSaveAction({ save: async () => {}, isSaving: false, canSave: false });
    }
  }, [onSaveAction]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const data = hedgesDerivedData.map((item, idx) => {
      // Calculate average percentage for display (or use first material's percentage)
      const percentages = item.percentages || {};
      const percentageValues = Object.values(percentages);
      const avgPercentage = percentageValues.length > 0 
        ? (percentageValues.reduce((sum, val) => sum + val, 0) / percentageValues.length * 100)
        : 0;
      
      const chartItem = {
        month: item.terminal,
        ...item.derivedExposures,
        covPercentage: avgPercentage.toFixed(1),
        percentages: item.percentages,
        absoluteMonth: item.absoluteMonth,
        // Carry full detail so tooltip, MonthDetail, and Show Calc all read from one source
        calculationDetails: item.calculationDetails,
        derivedExposures: item.derivedExposures,
      };
      
      
      return chartItem;
    });
    
    return data;
  }, [hedgesDerivedData, derivedMaterials]);

  // Notify parent when data changes — ref avoids infinite loop from unstable callback
  const onDataCalculatedRef = React.useRef(onDataCalculated);
  React.useEffect(() => { onDataCalculatedRef.current = onDataCalculated; }, [onDataCalculated]);
  React.useEffect(() => {
    if (!onDataCalculatedRef.current || !derivedMaterials || chartData.length === 0) return;
    onDataCalculatedRef.current({
      chartData,
      derivedMaterials: derivedMaterials.map(dm => ({
        formula_code: dm.formula_code,
        formula_name: dm.formula_name,
        price_ratio: dm.price_ratio,
      })),
      formulaColorDictionary,
    });
  }, [chartData, derivedMaterials, formulaColorDictionary]);

  // Prepare datasets for the chart
  const datasets = useMemo<DatasetConfig[]>(() => {
    if (!derivedMaterials || derivedMaterials.length === 0) {
      console.log('[HedgesDeriving] No derived materials for datasets');
      return [];
    }
    
    const ds = derivedMaterials.map((dm) => {
      // Get material color from formulaColorDictionary
      const backgroundColor = formulaColorDictionary[dm.formula_code] || '#6B7280';
      
      return {
        label: dm.formula_name,
        dataKey: dm.formula_code,
        type: 'bar' as const,
        backgroundColor,
        borderColor: backgroundColor,
        borderWidth: 1,
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0,
        },
        yAxisID: 'y',
      };
    });
    
    console.log('[HedgesDeriving] Datasets prepared:', {
      count: ds.length,
      datasets: ds,
    });
    
    return ds;
  }, [derivedMaterials, formulaColorDictionary]);

  // Keep a ref to chartData so plugins always read current data (no stale closures)
  const chartDataRef = React.useRef(chartData);
  React.useEffect(() => { chartDataRef.current = chartData; }, [chartData]);

  // Plugin to color On-Hand bars and add percentage labels
  // Stable factory (no deps) — plugins read from ref, so always current
  const pluginsFactory = useMemo(
    () => () => {
      const onHandColorPlugin = {
        id: 'hedgesOnHandColor',
        afterDatasetsDraw(chart: any) {
          const ctx = chart.ctx;
          
          chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            
            if (meta && meta.data && meta.data.length > 0 && !meta.hidden) {
              const firstBar = meta.data[0];
              if (firstBar && firstBar.y <= firstBar.base) {
                ctx.save();
                
                const { x, y, base, width } = firstBar;
                const barX = x - width / 2;
                const barY = y;
                const radius = 4;

                const materialColor = dataset.backgroundColor;
                const greenColor = '#10B981';

                ctx.fillStyle = materialColor;
                ctx.beginPath();
                ctx.moveTo(barX + radius, barY);
                ctx.lineTo(barX + width - radius, barY);
                ctx.quadraticCurveTo(barX + width, barY, barX + width, barY + radius);
                ctx.lineTo(barX + width, base);
                ctx.lineTo(barX, base);
                ctx.lineTo(barX, barY + radius);
                ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
                ctx.closePath();
                ctx.fill();
                
                ctx.strokeStyle = greenColor;
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(barX, base);
                ctx.lineTo(barX, barY + radius);
                ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
                ctx.lineTo(barX + width - radius, barY);
                ctx.quadraticCurveTo(barX + width, barY, barX + width, barY + radius);
                ctx.lineTo(barX + width, base);
                ctx.stroke();
                
                ctx.restore();
              }
            }
          });
        }
      };

      const percentageLabelPlugin = {
        id: 'hedgesPercentageLabels',
        afterDatasetsDraw(chart: any) {
          // Read from ref — always current, never stale
          const currentChartData = chartDataRef.current;
          const ctx = chart.ctx;
          const { scales } = chart;
          const xScale = scales.x;
          const yScale = scales.y;

          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          ctx.fillStyle = '#374151';

          const dataLength = chart.data.labels?.length || 0;
          
          for (let dataIndex = 0; dataIndex < dataLength; dataIndex++) {
            if (dataIndex === 0) continue;

            // Match by label name — works regardless of filtering
            const label = chart.data.labels?.[dataIndex];
            const chartDataItem = currentChartData.find((item: any) => item.month === label);
            if (!chartDataItem || !chartDataItem.percentages) continue;

            const percentages = chartDataItem.percentages;
            
            const hasData = Object.values(percentages).some(p => p > 0);
            if (!hasData) continue;
            
            const x = xScale.getPixelForValue(dataIndex);
            
            let maxY = yScale.bottom;
            chart.data.datasets.forEach((dataset: any, dsIndex: number) => {
              const meta = chart.getDatasetMeta(dsIndex);
              if (meta && meta.data && meta.data[dataIndex]) {
                const bar = meta.data[dataIndex];
                if (bar.y < maxY) {
                  maxY = bar.y;
                }
              }
            });

            const materialCodes = Object.keys(percentages);
            const yOffset = -8;
            
            materialCodes.forEach((formulaCode, idx) => {
              const percentage = percentages[formulaCode];
              if (percentage === 0) return;
              
              const percentageText = `${(percentage * 100).toFixed(0)}%`;
              const labelY = maxY + yOffset - (idx * 14);
              
              const dataset = chart.data.datasets[idx];
              ctx.fillStyle = dataset?.backgroundColor || '#374151';
              
              ctx.fillText(percentageText, x, labelY);
            });
          }

          ctx.restore();
        }
      };

      return [onHandColorPlugin, percentageLabelPlugin];
    },
    [], // stable — reads from chartDataRef
  );

  const terminalCount = useMemo(
    () => (terminals || []).length,
    [terminals],
  );

  if (!derivedMaterials || derivedMaterials.length === 0) {
    return (
      <div className="flex flex-col h-full p-4 items-center justify-center text-gray-500">
        <p className="text-sm">No derived materials configured yet.</p>
        <p className="text-xs mt-2">Add derived materials in the Strategy Details panel to see the chart.</p>
      </div>
    );
  }

  return (
    <>
      {/* Calculation Modal */}
      <Dialog open={showCalcModal} onClose={() => setShowCalcModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Hedge Allocation Calculation</DialogTitle>
        <DialogContent>
          <div className="space-y-4 py-2">
            <div className="bg-blue-50 p-3 rounded">
              <h3 className="font-semibold text-sm mb-2">Formula:</h3>
              <p className="text-xs font-mono">Monetary Exposure = Final Exposure (MT) × Spot Price × Price Ratio</p>
              <p className="text-xs font-mono mt-1">Allocation % = Material Exposure / Total Exposure</p>
            </div>
            
            {chartData.slice(1, 4).map((item, idx) => {
              if (!item.calculationDetails) return null;
              
              return (
              <div key={idx} className="border rounded p-3">
                <h3 className="font-semibold mb-2">{item.month}</h3>
                {derivedMaterials.map(dm => {
                  const details = item.calculationDetails?.[dm.formula_code];
                  const exposure = item.derivedExposures?.[dm.formula_code] || 0;
                  const percentage = item.percentages?.[dm.formula_code] || 0;
                  
                  return (
                    <div key={dm.formula_code} className="mb-2 text-xs">
                      <p className="font-semibold">{dm.formula_code}:</p>
                      {details && (
                        <div className="ml-2 space-y-0.5 font-mono text-xs">
                          <p>Demand: {details.demand.toFixed(2)} MT</p>
                          <p>+ Buffer: {details.buffer.toFixed(2)} MT</p>
                          <p>- Supply (Storage + POs): {details.supply.toFixed(2)} MT</p>
                          <p className="font-semibold">= Exposure: {details.finalExposure.toFixed(2)} MT</p>
                          <p>× Spot Price: ${details.spotPrice.toFixed(2)}</p>
                          <p>× Price Ratio: {details.ratio}</p>
                          <p className="font-semibold text-blue-600">= Monetary: ${exposure.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                          <p className="font-semibold text-green-600">→ Allocation: {(percentage * 100).toFixed(1)}%</p>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs font-semibold">
                    Total Exposure: ${Object.values(item.derivedExposures || {}).reduce((sum: number, val: number) => sum + val, 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </p>
                </div>
              </div>
              );
            })}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCalcModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <StrategyChart
      data={chartData}
      datasets={datasets}
      matPrice={baseMaterialPrice}
      unit={unit || { displayName: 'Metric Tons', shortForm: 'MT', convertPrice: (v: number) => v, convertMetricTonToUnit: (v: number) => v }}
      onTimeChange={onTimeChange || (() => {})}
      formulaId={baseMaterialId}
      initialGranularity={terminalCount > 0 ? 'Terminal' : 'Month'}
      onBarClick={(index) => {
        if (onMonthSelect) {
          // Subtract 1 to account for on-hand bar at index 0
          const adjustedIndex = Math.max(0, index - 1);
          onMonthSelect(adjustedIndex);
        }
      }}
      pluginsFactory={pluginsFactory}
      selectedIndex={selectedIndex != null ? selectedIndex + 1 : undefined}
      extraParams={{
        hedgesDeriving: true,
        derivedMaterials: derivedMaterials,
      }}
      renderControls={({ isQuantity, setIsQuantity, timeGranularity, setTimeGranularity, viewMonths, setViewMonths }) => (
        <ChartControls
          title="Hedges Deriving Policy"
          subtitle="Exposure for each derived material calculated by price ratios"
          isQuantity={isQuantity}
          setIsQuantity={setIsQuantity}
          timeGranularity={timeGranularity}
          setTimeGranularity={setTimeGranularity}
          viewMonths={viewMonths}
          setViewMonths={setViewMonths}
          granularityOptions={terminalCount > 0 ? ['Terminal', 'Yearly', 'Full'] : ['Yearly', 'Full']}
          disableGranularity={terminalCount > 0}
          hideValueQtyToggle={true}
          hideSubtitleInQtyMode={false}
          extraControls={
            <MuiTooltip title="Show allocation calculation details" arrow placement="top">
              <button
                onClick={() => setShowCalcModal(true)}
                className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <Code sx={{ fontSize: 17 }} />
              </button>
            </MuiTooltip>
          }
        />
      )}
    />
    </>
  );
};
